import React, { useEffect, useState, useCallback } from 'react'
import _ from 'lodash/fp'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import { builder, BuilderComponent } from '@builder.io/react'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'

import { actions as guideActions, selectors as guideSelectors } from '../../reducers/guides'
import * as ProfileReducer from '../../reducers/profile'

import { getHoleImage } from '../../config'
import colors from '@fe/ui/colors'
import { generateHoles } from '../../util/scorecard'

import Spinner from '@fe/ui/Spinner'
import ErrorBox from '@fe/ui/ErrorBox'
import ScoreCard from '../../components/Scorecard'
import { SpacerTitle } from '../../components/Texts'
import Landing from './Landing'
import { PointAvgChart } from './Charts'

import RespWidth from '../../components/withWidth'

import WebDragon from '@fe/seadragon/build/web'

import Stats from './Stats'

import { graphql } from 'react-apollo'
import { loader } from 'graphql.macro'
const gameQuery = loader('./getGame.gql')

builder.init('32eea804311a4d17817f2e534ded7af8')

const ScoreCardContainer = ({ holes, selectedHole, setSelectedHole, handicapComparison, flipHandicapComparison }) => {
  const activeStyle = { fontWeight: '700' }
  const inactiveStyle = { fontWeight: '300' }
  return (
    <div style={{ width: '100%' }}>
      <Grid item xs={12} style={{ overflowX: 'auto' }}>
        <ScoreCard
          holes={holes}
          selectedHole={selectedHole}
          setSelectedHole={setSelectedHole}
          noBottomBorder
          handicapComparison={handicapComparison}
        />
      </Grid>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
        <Button size='small' onClick={flipHandicapComparison} style={{ flexDirection: 'row', display: 'flex' }}>
          <Typography style={handicapComparison ? activeStyle : inactiveStyle}>CH</Typography>
          <Typography style={handicapComparison ? inactiveStyle : activeStyle}>PAR</Typography>
        </Button>
      </div>
    </div>
  )
}

const getFirstHole = _.flow(
  _.get('holes'),
  _.first,
  _.get('holeId')
)

const holeIdExists = holeId => _.flow(
  _.get('holes'),
  _.find({ holeId }),
  _.isObject
)

const getHoleId = ({ urlHoleNumber, urlHoleId, getGameDetails }) => {
  if (!urlHoleNumber && !urlHoleId) {
    return getFirstHole(getGameDetails)
  }
  if (urlHoleId && holeIdExists(_.toNumber(urlHoleId))(getGameDetails)) {
    return _.toNumber(urlHoleId)
  }
  return _.getOr(
    getFirstHole(getGameDetails),
    `holes.${_.toNumber(urlHoleNumber) - 1}.holeId`,
    getGameDetails
  )
}

const Game = ({ data: { loading, error, getGameDetails }, fetchGuideJson, getGuide, getClub, profile, classes, ...props }) => {
  const [selectedHole, setSelectedHole] = useState()
  const [pointerEvents, setPointerEvents] = useState('auto')
  const searchParams = new URLSearchParams(_.get('location.search', props))
  const urlHoleNumber = searchParams.get('hole')
  const urlHoleId = searchParams.get('holeId')

  const wakeTheDragon = useCallback(_.debounce(300, () => setPointerEvents('auto')), [setPointerEvents])

  useEffect(() => {
    window.scrollTo(0, 0)
    window.onscroll = (e) => {
      setPointerEvents('none')
      wakeTheDragon()
    }
  }, [setPointerEvents, wakeTheDragon])

  useEffect(() => {
    if (!(loading || error)) {
      if (urlHoleNumber || urlHoleId) {
        const holeId = getHoleId({ urlHoleNumber, urlHoleId, getGameDetails })
        setSelectedHole(holeId)

        // scrolling is a bit wonky while everything is loading
        // so wait a bit before scrolling
        setTimeout(() => {
          props.history.replace({ search: null }) // Remove hole link from URL
          window.scrollTo({ top: window.innerHeight - 64, behavior: 'smooth' })
        }, 500)
      } else {
        setSelectedHole(getFirstHole(getGameDetails))
      }
      const guideId = _.get('game.guideId', getGameDetails)
      fetchGuideJson({ guideId })
    }
  }, [loading])

  if (loading) {
    return <Spinner title='Loading game' />
  }
  if (error) {
    const errorMessages = _.flow(
      _.get('graphQLErrors'),
      _.map(_.get('message'))
    )(error)
    return <ErrorBox message={errorMessages} />
  }

  const guideTitle = _.get('game.guideTitle')(getGameDetails)
  const guideId = _.get('game.guideId')(getGameDetails)
  const guide = getGuide({ guideId }) || { title: guideTitle }

  const clubName = _.get('game.clubName')(getGameDetails)
  const club = { name: clubName, gallery: _.get('bannerUrl', getGameDetails) }
  const gamePlayedAt = 1000 * _.getOr(0, 'game.gamePlayedAt')(getGameDetails)
  const ratingStyle = _.get('game.ratingStyle', getGameDetails)

  const holes = generateHoles(getGameDetails, props.handicapComparison, ratingStyle)

  const page = _.flow(
    _.get('pages'),
    _.find({ hole_id: selectedHole })
  )(guide)

  const teeId = _.flow(
    _.find({ holeId: selectedHole }),
    _.get('teeId')
  )(holes)

  const tee = _.flow(
    _.get('tees'),
    _.find({ id: teeId })
  )(page)

  const teeLocation = _.get('position', tee)

  const shotLocations = _.flow(
    _.find({ holeId: selectedHole }),
    _.get('shots'),
    _.map(({ lat, lon }) => ({ location: { latitude: lat, longitude: lon } })),
    _.compact
  )(holes)
  const Chart = RespWidth(PointAvgChart)
  const holeImagePath = getHoleImage({ guideId, holeId: selectedHole })
  const height = Math.min(500, Math.max(window.innerHeight / 3, window.innerWidth / 2))
  const canvasHeight = _.get('height', guide) || _.get('boundingBox.canvas.lowerRight.y', page)
  const canvasWidth = _.get('width', guide) || _.get('boundingBox.canvas.lowerRight.x', page)

  const imgUrl = club.gallery
  const image = `url('${imgUrl}')`
  const placing = 'no-repeat center center/cover'
  return (
    <>
      <Helmet>
        <title>{_.get('title', guide)} - my.GLFR Game</title>
      </Helmet>
      <div style={{
        background: `${image} ${placing}`,
        backgroundAttachment: 'fixed',
        height: '100%'
      }}
      >
        <Landing holes={holes} club={club} guide={guide} date={gamePlayedAt} player={_.get('user', getGameDetails)} />
        <Grid container style={{ backgroundColor: '#f2f3f2', minHeight: 'calc(100vh - 112px)' }}>
          <Grid item xs={12} style={{ maxWidth: 1280, margin: 'auto', padding: '0 10px' }}>
            <SpacerTitle>Hole-by-hole</SpacerTitle>
          </Grid>
          <Grid key={selectedHole} item xs={12} style={{ minHeight: height, backgroundColor: colors.white, pointerEvents }}>
            {selectedHole && guide && page &&
              <WebDragon
                {...page}
                shotLocations={shotLocations}
                centerGreenLocation={_.get('center_green', page)}
                proj4text={_.get('proj4text', guide)}
                holeImagePath={holeImagePath}
                dimensions={{ height: canvasHeight, width: canvasWidth }}
                teeLocation={teeLocation}
              />}
          </Grid>

          <Grid item xs={12} container style={{ backgroundColor: 'white' }}>
            <ScoreCardContainer
              handicapComparison={props.handicapComparison}
              flipHandicapComparison={props.flipHandicapComparison}
              holes={holes}
              selectedHole={selectedHole}
              setSelectedHole={setSelectedHole}
            />
          </Grid>
        </Grid>
        <div className='outer-container'>
          <div className='container-1280'>
            <BuilderComponent
              model='content-sections'
              entry='758d6ca617e14fe7aefe7ef4c1a2a585'
            />

            <Grid style={{ backgroundColor: 'white', minHeight: 'calc(100vh - 112px)' }} container>
              <Grid item xs={12}>
                <SpacerTitle>Game Performance</SpacerTitle>
              </Grid>

              <Grid item xs={12} style={{ backgroundColor: 'white', paddingTop: 78 }}>
                <Stats gameDetails={getGameDetails} />
              </Grid>

              <Grid item xs={12} style={{ minHeight: height, display: 'flex', flexDirection: 'column' }}>
                <div classes={classes}>
                  {holes && selectedHole && <Chart holes={holes} selected={selectedHole} onClick={setSelectedHole} height={height} />}
                  <div style={{ margin: 6, fontSize: '1em' }}>
                    Your Hole by Hole Performance shows the ups and downs of your game.
                    The graph shows how many points you are off target with the red line as the baseline.
                    So an upward slope is great and a downward slope isn't.
                  </div>
                </div>
              </Grid>
            </Grid>

          </div>
        </div>
      </div>

    </>
  )
}

export default _.compose(
  connect(
    (s, ownProps) => ({
      gameHandle: _.get('match.params.gameId', ownProps),
      getGuide: ({ guideId }) => guideSelectors.guideById(guideId)(s),
      handicapComparison: ProfileReducer.selectors.handicapComparison(s)
    }),
    (d) => ({
      fetchGuideJson: ({ guideId }) => d(guideActions.guideJsonRequest({ guideId })),
      flipHandicapComparison: () => d(ProfileReducer.actions.flipHandicapComparison())
    })
  ),
  graphql(gameQuery)
)(Game)
